import { useEffect, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useLists } from "~/state/lists";
import { useTask, useTaskActions, useTaskCompletions } from "~/state/tasks";
import { useTaskStats } from "~/state/analytics";
import {
  CADENCE_OPTIONS,
  cadenceLabel,
  nextDueOn,
  WEEKDAY_LABELS,
} from "~/utils/cadence";
import type { TaskCadence } from "~/api/types";
import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

function Sparkline({ values }: { values: number[] }) {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  return (
    <View style={stylesheet.sparkline} accessibilityLabel="Completion history">
      {values.map((value, index) => (
        <View
          key={index}
          style={[
            stylesheet.sparkBar,
            {
              height: value ? 28 : 8,
              backgroundColor: value ? colors.success : colors.border,
            },
          ]}
        />
      ))}
    </View>
  );
}

export default function TaskDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const task = useTask(id);
  const lists = useLists();
  const actions = useTaskActions();
  const completions = useTaskCompletions(id);
  const stats = useTaskStats(id);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [cadence, setCadence] = useState<TaskCadence>("once");
  const [weekday, setWeekday] = useState(1);
  const [monthDay, setMonthDay] = useState("1");

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setNotes(task.notes ?? "");
    setCadence(task.cadence);
    setWeekday(task.weekday ?? new Date().getDay());
    setMonthDay(String(task.month_day ?? new Date().getDate()));
  }, [task]);

  if (!task) {
    return (
      <ProtectedScreen title="Task">
        <EmptyState title="This task is not on this device." />
      </ProtectedScreen>
    );
  }

  const currentTask = task;
  const list = lists.find((item) => item.id === currentTask.list_id);

  function save() {
    const month = Number(monthDay) || 1;
    actions.update(currentTask.id, {
      title,
      notes,
      cadence,
      weekday: cadence === "weekly" ? weekday : null,
      monthDay: cadence === "monthly" || cadence === "yearly" ? month : null,
      dueOn: nextDueOn({
        cadence,
        weekday,
        monthDay: month,
      }),
    });
  }

  return (
    <ProtectedScreen title={task.title} subtitle={list?.name ?? "Task"}>
      <Card gap="md">
        <SectionHeader title="Details" />
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Title
          </AppText>
          <TextField
            accessibilityLabel="Task title"
            value={title}
            onChangeText={setTitle}
            onBlur={save}
          />
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Notes
          </AppText>
          <TextField
            accessibilityLabel="Notes"
            value={notes}
            placeholder="Anything you need to remember"
            multiline
            style={stylesheet.textarea}
            onChangeText={setNotes}
            onBlur={save}
          />
        </View>
        <View style={stylesheet.field}>
          <AppText size="xs" weight="semibold">
            Repeats
          </AppText>
          <SegmentedControl
            value={cadence}
            options={CADENCE_OPTIONS}
            onChange={(value) => {
              setCadence(value);
              actions.update(task.id, {
                cadence: value,
                weekday: value === "weekly" ? weekday : null,
                monthDay:
                  value === "monthly" || value === "yearly"
                    ? Number(monthDay) || 1
                    : null,
                dueOn: nextDueOn({
                  cadence: value,
                  weekday,
                  monthDay: Number(monthDay) || 1,
                }),
              });
            }}
          />
          <AppText size="xs">
            {cadenceLabel(cadence, weekday, Number(monthDay) || null)} · next
            due {task.due_on}
          </AppText>
        </View>
        {cadence === "weekly" ? (
          <SegmentedControl
            value={String(weekday)}
            options={WEEKDAY_LABELS.map((label, index) => ({
              label: label.slice(0, 3),
              value: String(index),
            }))}
            onChange={(value) => {
              const next = Number(value);
              setWeekday(next);
              actions.update(task.id, {
                weekday: next,
                dueOn: nextDueOn({ cadence: "weekly", weekday: next }),
              });
            }}
          />
        ) : null}
        {cadence === "monthly" || cadence === "yearly" ? (
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Day of month
            </AppText>
            <TextField
              accessibilityLabel="Day of month"
              keyboardType="number-pad"
              value={monthDay}
              onChangeText={setMonthDay}
              onBlur={save}
            />
          </View>
        ) : null}
      </Card>

      <Card gap="md">
        <SectionHeader
          title="History"
          description="Completions over the last two weeks, plus the longer view."
        />
        {stats ? <Sparkline values={stats.sparkline} /> : null}
        <View style={stylesheet.metrics}>
          <View style={stylesheet.metric}>
            <MetricCard
              label="30-day rate"
              value={stats ? `${Math.round(stats.rate30 * 100)}%` : "—"}
            />
          </View>
          <View style={stylesheet.metric}>
            <MetricCard
              label="90-day rate"
              value={stats ? `${Math.round(stats.rate90 * 100)}%` : "—"}
            />
          </View>
          <View style={stylesheet.metric}>
            <MetricCard label="Streak" value={String(stats?.streak ?? 0)} />
          </View>
        </View>
        {completions.length === 0 ? (
          <EmptyState title="No completions yet" />
        ) : (
          <Stack gap="xs">
            {completions
              .slice()
              .reverse()
              .slice(0, 12)
              .map((row) => (
                <Badge key={row.id} tone="success">
                  {row.completed_on}
                </Badge>
              ))}
          </Stack>
        )}
      </Card>

      <Card gap="sm">
        <Button
          variant="danger"
          align="start"
          onPress={() => {
            actions.remove(task.id);
            router.replace(`/lists/${task.list_id}` as never);
          }}
        >
          <ButtonText>Delete task</ButtonText>
        </Button>
      </Card>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    field: { gap: spacing.xs },
    textarea: { minHeight: 86, textAlignVertical: "top" },
    metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    metric: { minWidth: 120, flex: 1 },
    sparkline: {
      flexDirection: "row",
      alignItems: "flex-end",
      gap: 4,
      height: 32,
    },
    sparkBar: {
      flex: 1,
      borderRadius: radii.xs,
    },
  });
